import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { printerService } from '@/services/printer.service';
import type {
  PrinterConfig,
  PrintJob,
  ConnectionStatus,
  PrinterConnectionType,
  PaperWidth,
  PrinterDeviceInfo,
  ReceiptBillData,
  ReceiptTenantData,
  ReceiptLocationData,
  ReceiptOptions,
  ReceiptTaxPresentation,
  KotData,
  PrintResult,
  PaymentReceiptData,
} from '@/lib/escpos';
import type { CurrencyCode } from '@bbpos/types';
import { DEFAULT_PRINTER_CONFIG } from '@/lib/escpos';

// ─── State ──────────────────────────────────────────────────────────────────

interface PrinterState {
  // Connection
  connectionStatus: ConnectionStatus;
  connectionType: PrinterConnectionType;
  printerName: string | null;
  printerIp: string | null;
  printerPort: number;
  deviceInfo: PrinterDeviceInfo | null;

  // Settings
  paperWidth: PaperWidth;
  autoPrint: boolean;
  autoCut: boolean;
  copies: number;
  kickCashDrawer: boolean;

  // Queue
  printQueue: PrintJob[];
  lastError: string | null;

  // Actions
  configure: (config: Partial<PrinterConfig>) => void;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  printReceipt: (
    bill: ReceiptBillData,
    tenant: ReceiptTenantData,
    location: ReceiptLocationData,
    tax?: ReceiptTaxPresentation,
  ) => PrintResult;
  printDuplicate: (
    bill: ReceiptBillData,
    tenant: ReceiptTenantData,
    location: ReceiptLocationData,
    tax?: ReceiptTaxPresentation,
  ) => PrintResult;
  printKot: (kot: KotData) => PrintResult;
  /** L03: the slip for a payment on account, at this printer's paper width. */
  printPaymentReceipt: (data: PaymentReceiptData, receiptId: string, currency: CurrencyCode) => PrintResult;
  printTest: () => PrintResult;
  openCashDrawer: () => Promise<void>;
  clearQueue: () => void;
  retryFailed: () => void;
  setError: (error: string | null) => void;
  hydrate: () => void;
  reset: () => void;
}

// ─── Store ──────────────────────────────────────────────────────────────────

export const usePrinterStore = create<PrinterState>()(
  devtools(
    (set, get) => {
      const handleStatusChange = (status: ConnectionStatus) => {
        set({ connectionStatus: status });
        if (status === 'connected') {
          const info = printerService.getTransport()?.getDeviceInfo() ?? null;
          set({ deviceInfo: info, printerName: info?.name ?? null, lastError: null });
        }
      };

      const handleQueueChange = (queue: PrintJob[]) => {
        set({ printQueue: [...queue] });
      };

      const getConfig = (): PrinterConfig => {
        const s = get();
        return {
          connectionType: s.connectionType,
          paperWidth: s.paperWidth,
          printerName: s.printerName,
          printerIp: s.printerIp,
          printerPort: s.printerPort,
          autoPrint: s.autoPrint,
          autoCut: s.autoCut,
          copies: s.copies,
          kickCashDrawer: s.kickCashDrawer,
        };
      };

      const getReceiptOptions = (): ReceiptOptions => {
        const s = get();
        return {
          paperWidth: s.paperWidth,
          printQr: true,
        };
      };

      return {
        // Initial state
        connectionStatus: 'disconnected',
        connectionType: DEFAULT_PRINTER_CONFIG.connectionType,
        printerName: null,
        printerIp: null,
        printerPort: DEFAULT_PRINTER_CONFIG.printerPort,
        deviceInfo: null,
        paperWidth: DEFAULT_PRINTER_CONFIG.paperWidth,
        autoPrint: DEFAULT_PRINTER_CONFIG.autoPrint,
        autoCut: DEFAULT_PRINTER_CONFIG.autoCut,
        copies: DEFAULT_PRINTER_CONFIG.copies,
        kickCashDrawer: DEFAULT_PRINTER_CONFIG.kickCashDrawer,
        printQueue: [],
        lastError: null,

        configure(config) {
          set((s) => {
            const merged: Partial<PrinterState> = {};
            if (config.connectionType !== undefined) merged.connectionType = config.connectionType;
            if (config.paperWidth !== undefined) merged.paperWidth = config.paperWidth;
            if (config.printerName !== undefined) merged.printerName = config.printerName;
            if (config.printerIp !== undefined) merged.printerIp = config.printerIp;
            if (config.printerPort !== undefined) merged.printerPort = config.printerPort;
            if (config.autoPrint !== undefined) merged.autoPrint = config.autoPrint;
            if (config.autoCut !== undefined) merged.autoCut = config.autoCut;
            if (config.copies !== undefined) merged.copies = config.copies;
            if (config.kickCashDrawer !== undefined) merged.kickCashDrawer = config.kickCashDrawer;
            return merged;
          });
          // Persist
          const fullConfig = { ...getConfig(), ...config };
          printerService.saveConfig(fullConfig);
        },

        async connect() {
          const config = getConfig();
          set({ lastError: null });
          try {
            await printerService.connect(config, handleStatusChange);
          } catch (err) {
            const msg = err instanceof Error ? err.message : 'Connection failed';
            set({ lastError: msg, connectionStatus: 'error' });
            throw err;
          }
        },

        async disconnect() {
          await printerService.disconnect(handleStatusChange);
          set({ deviceInfo: null });
        },

        printReceipt(bill, tenant, location, tax) {
          const s = get();
          const options = getReceiptOptions();
          // Same origin as the running app, so the QR is right on localhost and
          // on each hosted domain. TODO: /receipt/:id is not a route yet.
          options.qrUrl = `${window.location.origin}/receipt/${bill.id}`;
          return printerService.printReceipt(
            bill, tenant, location, options, s.copies,
            handleQueueChange, handleStatusChange, tax,
          );
        },

        printDuplicate(bill, tenant, location, tax) {
          const s = get();
          const options = getReceiptOptions();
          // Same origin as the running app, so the QR is right on localhost and
          // on each hosted domain. TODO: /receipt/:id is not a route yet.
          options.qrUrl = `${window.location.origin}/receipt/${bill.id}`;
          return printerService.printDuplicate(
            bill, tenant, location, options, s.copies,
            handleQueueChange, handleStatusChange, tax,
          );
        },

        printKot(kot) {
          const s = get();
          return printerService.printKot(
            kot, s.paperWidth, s.copies,
            handleQueueChange, handleStatusChange,
          );
        },

        printPaymentReceipt(data, receiptId, currency) {
          const s = get();
          return printerService.printPaymentReceipt(
            data, receiptId, s.paperWidth, currency, s.copies,
            handleQueueChange, handleStatusChange,
          );
        },

        printTest() {
          const s = get();
          return printerService.printTest(
            s.paperWidth,
            handleQueueChange, handleStatusChange,
          );
        },

        async openCashDrawer() {
          try {
            await printerService.kickCashDrawer();
          } catch (err) {
            const msg = err instanceof Error ? err.message : 'Cash drawer kick failed';
            set({ lastError: msg });
          }
        },

        clearQueue() {
          printerService.clearQueue(handleQueueChange);
        },

        retryFailed() {
          printerService.retryFailed(handleQueueChange, handleStatusChange);
        },

        setError(error) {
          set({ lastError: error });
        },

        hydrate() {
          const saved = printerService.loadConfig();
          if (saved) {
            set({
              connectionType: saved.connectionType,
              paperWidth: saved.paperWidth,
              printerName: saved.printerName,
              printerIp: saved.printerIp,
              printerPort: saved.printerPort,
              autoPrint: saved.autoPrint,
              autoCut: saved.autoCut,
              copies: saved.copies,
              kickCashDrawer: saved.kickCashDrawer,
            });
          }
          // Load any persisted queue
          const queue = printerService.getQueue();
          if (queue.length > 0) {
            set({ printQueue: queue });
          }
        },

        reset() {
          printerService.disconnect().catch(() => {});
          printerService.clearQueue();
          set({
            connectionStatus: 'disconnected',
            connectionType: DEFAULT_PRINTER_CONFIG.connectionType,
            printerName: null,
            printerIp: null,
            printerPort: DEFAULT_PRINTER_CONFIG.printerPort,
            deviceInfo: null,
            paperWidth: DEFAULT_PRINTER_CONFIG.paperWidth,
            autoPrint: DEFAULT_PRINTER_CONFIG.autoPrint,
            autoCut: DEFAULT_PRINTER_CONFIG.autoCut,
            copies: DEFAULT_PRINTER_CONFIG.copies,
            kickCashDrawer: DEFAULT_PRINTER_CONFIG.kickCashDrawer,
            printQueue: [],
            lastError: null,
          });
        },
      };
    },
    { name: 'printer-store' },
  ),
);
